import { useEffect, useState } from 'react'
import { PlusCircleFilled, DeleteOutlined, SaveOutlined, CloseOutlined, HolderOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { router } from '@inertiajs/react'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import axios from '@/lib/axios'
import dayjs from 'dayjs'
import { ZeroState } from '@/components/patterns'
import { Select, Input, DatePicker, message, Button, IconButton, ConfirmModal } from '@/components/ui'
import ColorPicker from './ColorPicker'

interface Event {
  id: number
  name: string
  slug: string
  start_date: string
  end_date: string
}

interface Track {
  id: number
  name: string
  color: string
  position: number
}

interface AgendaDay {
  id: number
  scheduled_on: string
  position: number
  tracks: Track[]
}

interface TracksTabProps {
  event: Event
  agendaDays: AgendaDay[]
  /** Open the in-place Builder for the given day. */
  onOpenBuilder?: (dayId: number) => void
}

interface NewTrack {
  name: string
  color: string
}

interface EditingTrack {
  id: number
  name: string
  originalName: string
}

interface SortableTrackRowProps {
  track: Track
  editingTrack: EditingTrack | null
  onStartEdit: (track: Track) => void
  onNameChange: (name: string) => void
  onSave: () => void
  onCancel: () => void
  onColorChange: (trackId: number, color: string) => void
  onDelete: (trackId: number, trackName: string) => void
}

/**
 * A single track row wrapped with @dnd-kit sortable behaviour. Only the leading
 * HolderOutlined handle activates dragging (via setActivatorNodeRef), so the inline
 * name edit, colour picker, save/cancel and delete controls keep working normally.
 */
function SortableTrackRow({
  track,
  editingTrack,
  onStartEdit,
  onNameChange,
  onSave,
  onCancel,
  onColorChange,
  onDelete,
}: SortableTrackRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: track.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...(isDragging ? { zIndex: 5 } : {}),
  }

  const isEditing = editingTrack?.id === track.id
  const hasChanges = isEditing && editingTrack.name !== editingTrack.originalName

  return (
    <div ref={setNodeRef} style={style} className="flex gap-2 items-center bg-white">
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="shrink-0 w-6 py-2 text-gray-400 hover:text-gray-700 cursor-move touch-none flex items-center justify-center"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <HolderOutlined />
      </button>
      <div className="w-full">
        <Input
          value={isEditing ? editingTrack.name : track.name}
          onChange={(e) => {
            if (isEditing) {
              onNameChange(e.target.value)
            } else {
              onStartEdit(track)
            }
          }}
          onFocus={() => {
            if (!isEditing) {
              onStartEdit(track)
            }
          }}
        />
      </div>
      <div>
        <ColorPicker
          value={track.color}
          onChange={(color) => onColorChange(track.id, color)}
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        {hasChanges && (
          <>
            <Button
              variant="primary"
              icon={<SaveOutlined />}
              size="small"
              onClick={onSave}
            >
              Save
            </Button>
            <IconButton
              icon={<CloseOutlined />}
              size="small"
              onClick={onCancel}
              ariaLabel="Cancel edit"
              title="Cancel"
            />
          </>
        )}
        <IconButton
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => onDelete(track.id, track.name)}
          ariaLabel="Delete track"
          title="Delete"
        />
      </div>
    </div>
  )
}

export default function TracksTab({ event, agendaDays, onOpenBuilder }: TracksTabProps) {
  // Local copy of the days/tracks so drag-to-reorder can update optimistically.
  // Resynced from the prop below whenever create/update/delete triggers a router.reload().
  const [days, setDays] = useState<AgendaDay[]>(agendaDays)
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null)
  const [filterDate, setFilterDate] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [newTracksByDay, setNewTracksByDay] = useState<Record<number, NewTrack>>({})
  const [editingTrack, setEditingTrack] = useState<EditingTrack | null>(null)
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; trackId: number | null; trackName: string }>({
    visible: false,
    trackId: null,
    trackName: ''
  })

  // Resync local state when the agendaDays prop changes (e.g. after a router.reload).
  useEffect(() => {
    setDays(agendaDays)
  }, [agendaDays])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const handleAddDate = async () => {
    if (!selectedDate) {
      message.warning('Please select a date')
      return
    }

    setLoading(true)
    try {
      const response = await axios.post(`/events/${event.slug}/agenda/days`, {
        day: {
          scheduled_on: selectedDate.format('YYYY-MM-DD')
        }
      })

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
        setSelectedDate(null)
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to add date')
    } finally {
      setLoading(false)
    }
  }

  const handleAddTrackClick = (dayId: number) => {
    setNewTracksByDay({
      ...newTracksByDay,
      [dayId]: { name: '', color: '#111827' }
    })
  }

  const handleCancelNewTrack = (dayId: number) => {
    const updated = { ...newTracksByDay }
    delete updated[dayId]
    setNewTracksByDay(updated)
  }

  const handleSaveNewTrack = async (dayId: number) => {
    const newTrack = newTracksByDay[dayId]
    if (!newTrack || !newTrack.name.trim()) {
      message.warning('Please enter a track name')
      return
    }

    try {
      const response = await axios.post(`/events/${event.slug}/agenda/days/${dayId}/tracks`, {
        track: {
          name: newTrack.name,
          color: newTrack.color
        }
      })

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
        handleCancelNewTrack(dayId)
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to add track')
    }
  }

  const handleTrackNameChange = (track: Track) => {
    setEditingTrack({
      id: track.id,
      name: track.name,
      originalName: track.name
    })
  }

  const handleEditingNameChange = (name: string) => {
    setEditingTrack(prev => (prev ? { ...prev, name } : prev))
  }

  const handleSaveTrackName = async () => {
    if (!editingTrack) return

    if (!editingTrack.name.trim()) {
      message.error('Track name cannot be empty')
      setEditingTrack(null)
      router.reload()
      return
    }

    try {
      const response = await axios.patch(`/events/${event.slug}/agenda/tracks/${editingTrack.id}`, {
        track: {
          name: editingTrack.name
        }
      })

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
        setEditingTrack(null)
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to update track')
      setEditingTrack(null)
      router.reload()
    }
  }

  const handleCancelEditTrackName = () => {
    setEditingTrack(null)
    router.reload()
  }

  const handleUpdateTrackColor = async (trackId: number, color: string) => {
    try {
      const response = await axios.patch(`/events/${event.slug}/agenda/tracks/${trackId}`, {
        track: {
          color: color
        }
      })

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to update track')
    }
  }

  const handleDeleteTrackClick = (trackId: number, trackName: string) => {
    setDeleteModal({ visible: true, trackId, trackName })
  }

  const handleDeleteTrack = async () => {
    if (!deleteModal.trackId) return

    try {
      const response = await axios.delete(`/events/${event.slug}/agenda/tracks/${deleteModal.trackId}`)

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to delete track')
    } finally {
      setDeleteModal({ visible: false, trackId: null, trackName: '' })
    }
  }

  /**
   * Reorder tracks within a single day. Curried so the day is captured per DndContext;
   * because each day has its own context/items, a drag can only ever reorder within that day.
   */
  const handleDragEnd = (dayId: number) => async (dragEvent: DragEndEvent) => {
    const { active, over } = dragEvent
    if (!over || active.id === over.id) return

    const day = days.find(d => d.id === dayId)
    if (!day) return

    const oldIndex = day.tracks.findIndex(t => t.id === active.id)
    const newIndex = day.tracks.findIndex(t => t.id === over.id)
    const overTrack = day.tracks[newIndex]
    if (oldIndex === -1 || newIndex === -1 || !overTrack) return

    // Snapshot for revert, then optimistically reorder THIS day's tracks only.
    const previousDays = days
    setDays(prev =>
      prev.map(d => (d.id === dayId ? { ...d, tracks: arrayMove(d.tracks, oldIndex, newIndex) } : d))
    )

    try {
      const res = await axios.patch(
        `/events/${event.slug}/agenda/tracks/${active.id}/reorder`,
        { position: overTrack.position }
      )
      if (res.data.success) {
        const returnedDayId = res.data.agenda_day_id as number
        const returnedTracks = res.data.tracks as Track[]
        setDays(prev =>
          prev.map(d => (d.id === returnedDayId ? { ...d, tracks: returnedTracks } : d))
        )
      } else {
        setDays(previousDays)
        message.error(res.data.errors?.join(', ') || 'Failed to reorder track')
      }
    } catch (e: any) {
      setDays(previousDays)
      message.error(e.response?.data?.errors?.join(', ') || 'Failed to reorder track')
    }
  }

  const filteredDays = filterDate
    ? days.filter(day => day.scheduled_on === filterDate)
    : days

  const dateOptions = days.map(day => ({
    label: dayjs(day.scheduled_on).format('D MMMM YYYY'),
    value: day.scheduled_on
  }))

  if (days.length === 0) {
    return (
      <div>
        <div className="flex gap-2 mb-6">
          <DatePicker
            value={selectedDate}
            onChange={setSelectedDate}
            format="YYYY-MM-DD"
            placeholder="Select Date"
            className="flex-1"
            size="small"
            minDate={dayjs(event.start_date)}
            maxDate={dayjs(event.end_date)}
          />
          <Button variant="primary" onClick={handleAddDate} loading={loading}>
            Add Date
          </Button>
        </div>

        <ZeroState
          icon={<span>📅</span>}
          title="No Tracks Yet"
          description="Add a date to get started with creating tracks"
        />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <DatePicker
            value={selectedDate}
            onChange={setSelectedDate}
            format="YYYY-MM-DD"
            placeholder="Select Date"
            style={{ width: 200 }}
            size="small"
            minDate={dayjs(event.start_date)}
            maxDate={dayjs(event.end_date)}
          />
          <Button variant="primary" onClick={handleAddDate} loading={loading}>
            Add Date
          </Button>
        </div>
        {days.length > 1 && (
          <Select
            placeholder="Filter by Date"
            allowClear
            value={filterDate}
            onChange={setFilterDate}
            options={dateOptions}
            style={{ width: 160 }}
            block={false}
          />
        )}
      </div>

      <div className="space-y-6">
        {filteredDays.map(day => (
          <div key={day.id} className="bg-white p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-600">
                {dayjs(day.scheduled_on).format('D MMMM YYYY')}
              </h3>
              {onOpenBuilder && (
                <Button
                  variant="primary"
                  size="small"
                  icon={<ArrowRightOutlined />}
                  iconPosition="end"
                  onClick={() => onOpenBuilder(day.id)}
                >
                  Open Builder
                </Button>
              )}
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex gap-2 text-sm font-medium text-gray-600 px-2">
                <div className="w-6 shrink-0"></div>
                <div className="w-full">Track Name</div>
                <div className="w-24">Track Colour</div>
                <div></div>
              </div>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd(day.id)}>
                <SortableContext items={day.tracks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-3">
                    {day.tracks.map(track => (
                      <SortableTrackRow
                        key={track.id}
                        track={track}
                        editingTrack={editingTrack}
                        onStartEdit={handleTrackNameChange}
                        onNameChange={handleEditingNameChange}
                        onSave={handleSaveTrackName}
                        onCancel={handleCancelEditTrackName}
                        onColorChange={handleUpdateTrackColor}
                        onDelete={handleDeleteTrackClick}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {/* New track form */}
              {newTracksByDay[day.id] && (
                <div className="grid grid-cols-12 gap-2 border-t border-gray-200 pt-3">
                  <div className="col-span-5">
                    <Input
                      placeholder="Enter Track Name"
                      value={newTracksByDay[day.id].name}
                      onChange={(e) => setNewTracksByDay({
                        ...newTracksByDay,
                        [day.id]: { ...newTracksByDay[day.id], name: e.target.value }
                      })}
                      autoFocus
                    />
                  </div>
                  <div className="col-span-5">
                    <ColorPicker
                      value={newTracksByDay[day.id].color}
                      onChange={(color) => setNewTracksByDay({
                        ...newTracksByDay,
                        [day.id]: { ...newTracksByDay[day.id], color }
                      })}
                    />
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-2">
                    <Button
                      variant="primary"
                      icon={<SaveOutlined />}
                      size="small"
                      onClick={() => handleSaveNewTrack(day.id)}
                    >
                      Save
                    </Button>
                    <IconButton
                      icon={<CloseOutlined />}
                      size="small"
                      onClick={() => handleCancelNewTrack(day.id)}
                      ariaLabel="Cancel new track"
                      title="Cancel"
                    />
                  </div>
                </div>
              )}
            </div>

            {!newTracksByDay[day.id] && (
              <Button
                variant="secondary"
                icon={<PlusCircleFilled />}
                onClick={() => handleAddTrackClick(day.id)}
                block
              >
                Add Track
              </Button>
            )}
          </div>
        ))}
      </div>

      <ConfirmModal
        open={deleteModal.visible}
        title="Delete track"
        onConfirm={handleDeleteTrack}
        onClose={() => setDeleteModal({ visible: false, trackId: null, trackName: '' })}
        confirmText="Yes"
        danger
      >
        Are you sure you want to delete this track?
      </ConfirmModal>
    </div>
  )
}
